import React, { createContext, useCallback, useContext, useState } from 'react';

type ComplementItem = {
  id: number | string;
  name?: string;
  order: number;
  rawMaterialId?: number;
  maximumQuantity: number;
  minimumQuantity: number;
  image?: File | string;
  price: number;
  pauseSale?: boolean;
};

type Complement = {
  id?: number | string;
  name?: string;
  order?: number;
  maximumQuantity?: number;
  minimumQuantity?: number;
  isRequired?: boolean;
  items?: ComplementItem[];
  pauseSale?: boolean;
};

export type Option = {
  value: number;
  label: string;
};

export type AvailabilityDetails = {
  days: {
    sunday: boolean;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
  };
  shifts: {
    startTime: number;
    endTime: number;
  }[];
};

export type Image = {
  id?: number;
  file?: File | string;
};

type Product = {
  id?: number;
  image?: File | string;
  name?: string;
  code?: string;
  gtin?: string;
  price: number;
  width?: number;
  height?: number;
  length?: number;
  weight?: number;
  images: Array<Image | undefined>;
  groupId?: number;
  newPrice: number;
  productPortion: boolean;
  portion?: number;
  hasDiscount: boolean;
  observation?: string;
  currentStock?: number;
  minimumStock?: number;
  categoriaFiscalId?: number | null;
  ncmOverride?: string | null;
  cfopOverride?: string | null;
  cestOverride?: string | null;
  origemOverride?: string | null;
  complements?: Complement[];
  isAwaysAvailability?: boolean;
  unitOfMeasurementId?: number;
  details: AvailabilityDetails;
};

type SimpleProductContextData = Product & {
  groupsOptions: Option[];
  isDrawerOpened?: boolean;
  complementsDrawerTab: 0 | 1;
  currentTab: 0 | 1 | 2 | 3 | 4 | 5;
  unitsOfMeasurementOptions: Option[];
  isComplementsDrawerOpened?: boolean;
  handleChangeProductPortion(value: boolean): void;
  handleAddShift(): void;
  handleOpenDrawer(): void;
  handleCloseDrawer(): void;
  handleResetProduct(): void;
  handleChangeTab(tab: number): void;
  handleOpenComplementsDrawer(): void;
  handleCloseComplementsDrawer(): void;
  handleRemoveShift(shiftIndex: number): void;
  handleAddComplement(complement: Complement): void;
  handleEditComplement(complement: Complement): void;
  handleChangeComplementsDrawerTab(tab: 0 | 1): void;
  handleChangeGroupsOptions(options: Option[]): void;
  handleChangeImage(index: number, image?: File): void;
  handleDeleteComplement(complement: Complement): void;
  handleChangeProduct(partialProduct: Partial<Product>): void;
  handleChangeUnitsOfMeasurementOptions(options: Option[]): void;
  handleChangeAvailabilityDays(days: AvailabilityDetails['days']): void;
  handleChangeShift(
    fieldName: keyof AvailabilityDetails['shifts'][0],
    value: number,
    shiftIndex: number,
  ): void;
};

const SimpleProductContext = createContext<SimpleProductContextData>(
  {} as SimpleProductContextData,
);

const INITIAL_PRODUCT_DATA = {
  unitOfMeasurementId: 6,
  isAwaysAvailability: true,
  images: Array(6).fill(undefined),
  price: 0,
  newPrice: 0,
  hasDiscount: false,
  productPortion: false,
  details: {
    days: {
      sunday: true,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
    },
    shifts: [],
  },
};

const SimpleProductProvider: React.FC = ({ children }) => {
  const [currentTab, setCurrentTab] = useState<0 | 1 | 2 | 3 | 4 | 5>(0);
  const [complementsDrawerTab, setComplementsDrawerTab] = useState<0 | 1>(0);
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean | undefined>(
    undefined,
  );
  const [isComplementsDrawerOpened, setIsComplementsDrawerOpened] = useState<
    boolean | undefined
  >(undefined);
  const [product, setProduct] = useState<Product>(INITIAL_PRODUCT_DATA);
  const [unitsOfMeasurementOptions, setUnitsOfMeasurementOptions] = useState<
    Option[]
  >([]);
  const [groupsOptions, setGroupsOptions] = useState<Option[]>([]);

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpened(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpened(false);
  }, []);

  const handleOpenComplementsDrawer = useCallback(() => {
    setIsComplementsDrawerOpened(true);
  }, []);

  const handleCloseComplementsDrawer = useCallback(() => {
    setIsComplementsDrawerOpened(false);
  }, []);

  const handleChangeGroupsOptions = useCallback((options: Option[]) => {
    setGroupsOptions(options);
  }, []);

  const handleChangeUnitsOfMeasurementOptions = useCallback(
    (options: Option[]) => {
      setUnitsOfMeasurementOptions(options);
    },
    [],
  );

  const handleAddComplement = useCallback((complement: Complement) => {
    setProduct((oldState) => ({
      ...oldState,
      complements: [...(oldState?.complements || []), complement],
    }));
  }, []);

  const handleDeleteComplement = useCallback((complement: Complement) => {
    setProduct((oldState) => ({
      ...oldState,
      complements: (oldState?.complements || []).filter(
        (oldComplement) => oldComplement.id !== complement.id,
      ),
    }));
  }, []);

  const handleEditComplement = useCallback((complement: Complement) => {
    setProduct((oldState) => ({
      ...oldState,
      complements: (oldState?.complements || []).map((oldComplement) => {
        if (oldComplement.id === complement.id) {
          return complement;
        }

        return oldComplement;
      }),
    }));
  }, []);

  const handleChangeProduct = useCallback(
    (partialProduct: Partial<Product>) => {
      setProduct((oldState) => ({ ...oldState, ...partialProduct }));
    },
    [],
  );

  const handleResetProduct = useCallback(() => {
    setProduct(INITIAL_PRODUCT_DATA);
  }, []);

  const handleAddShift = useCallback(() => {
    setProduct((oldState) => ({
      ...oldState,
      details: {
        days: oldState.details.days,
        shifts: [...oldState.details.shifts, { startTime: 1, endTime: 23 }],
      },
    }));
  }, []);

  const handleChangeShift = useCallback(
    (
      fieldName: keyof AvailabilityDetails['shifts'][0],
      value: number,
      shiftIndex: number,
    ) => {
      setProduct((oldState) => ({
        ...oldState,
        details: {
          days: oldState.details.days,
          shifts: oldState.details.shifts.map((shift, index) => {
            if (shiftIndex === index) {
              return {
                ...shift,
                [fieldName]: value,
              };
            }

            return shift;
          }),
        },
      }));
    },
    [],
  );

  const handleRemoveShift = useCallback((shiftIndex: number) => {
    setProduct((oldState) => ({
      ...oldState,
      details: {
        days: oldState.details.days,
        shifts: oldState.details.shifts.filter(
          (_shift, index) => shiftIndex !== index,
        ),
      },
    }));
  }, []);

  const handleChangeTab = useCallback((tab: 0 | 1 | 2 | 3 | 4 | 5) => {
    setCurrentTab(tab);
  }, []);

  const handleChangeComplementsDrawerTab = useCallback((tab: 0 | 1) => {
    setComplementsDrawerTab(tab);
  }, []);

  const handleChangeAvailabilityDays = useCallback(
    (days: AvailabilityDetails['days']) => {
      setProduct((oldState) => ({
        ...oldState,
        details: { ...oldState.details, days },
      }));
    },
    [],
  );

  const handleChangeProductPortion = useCallback((value: boolean) => {
    setProduct((oldState) => ({
      ...oldState,
      productPortion: value,
    }));
  }, []);

  const handleChangeImage = useCallback(
    (index: number, imageToChange?: File) => {
      setProduct((oldState) => ({
        ...oldState,
        images: oldState.images.map((image, imageIndex) => {
          if (imageIndex === index) {
            if (!imageToChange && !image?.id) {
              return undefined;
            }

            return {
              ...image,
              file: imageToChange,
            };
          }

          return image;
        }),
      }));
    },
    [],
  );

  return (
    <SimpleProductContext.Provider
      value={{
        currentTab,
        groupsOptions,
        isDrawerOpened,
        complementsDrawerTab,
        isComplementsDrawerOpened,
        unitsOfMeasurementOptions,
        handleChangeProductPortion,
        handleAddShift,
        handleChangeTab,
        handleOpenDrawer,
        handleChangeShift,
        handleCloseDrawer,
        handleChangeImage,
        handleRemoveShift,
        handleResetProduct,
        handleChangeProduct,
        handleAddComplement,
        handleEditComplement,
        handleDeleteComplement,
        handleChangeGroupsOptions,
        handleOpenComplementsDrawer,
        handleChangeAvailabilityDays,
        handleCloseComplementsDrawer,
        handleChangeComplementsDrawerTab,
        handleChangeUnitsOfMeasurementOptions,
        ...product,
      }}
    >
      {children}
    </SimpleProductContext.Provider>
  );
};

function useSimpleProduct(): SimpleProductContextData {
  const context = useContext(SimpleProductContext);

  if (!context)
    throw new Error(
      'useSimpleProduct must be used within SimpleProductProvider',
    );

  return context;
}

export { SimpleProductProvider, useSimpleProduct };
